import { useMemo, type ReactNode } from 'react';
import { useMatches } from 'react-router';
import { EnquiryFormPageType } from 'generated/graphql';

import {
    PROFESSIONAL_DETAILS_BASE_ROUTE_ID,
    PREVIEW_BASE_ROUTE_ID,
} from '@modules/business-manager/config/constants';
import type { ProfessionalDetailRootData } from '@modules/professional/page/ProfessionalPage';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { isProductSupplierProfessional } from '@modules/professional/util/is-product-supplier-professional';
import {
    PageCtasProvider,
    usePageCtas,
    type PageBookIntentBase,
    type PageCtasProviderProps,
} from '~/modules/enquiry/component/PageCtasProvider';
import { CtaAutoOpen } from '~/modules/enquiry/component/CtaAutoOpen';
import type { EnquiryModalContext } from '~/modules/enquiry/hooks/use-enquiry-modal';
import {
    useBookCta,
    useEnquireCta,
    type BookCta,
    type BookCtaOptions,
} from '~/modules/enquiry/hooks/use-page-ctas';
import { isBookableSession } from './utils/book-meeting';

/** The primary CTA's words while the professional has chosen none. */
const DEFAULT_ENQUIRE_LABEL = 'Get in Touch';

/** The root loader's `pageCtas`, from the public or the preview route. */
const useLoadedPageCtas = () => {
    const matches = useMatches();
    const route = matches.find((match) =>
        [PROFESSIONAL_DETAILS_BASE_ROUTE_ID, PREVIEW_BASE_ROUTE_ID].includes(
            match.id,
        ),
    );
    return (route?.data as ProfessionalDetailRootData | undefined)?.pageCtas;
};

/**
 * Resolves the professional page's Enquire and Book a meeting buttons once for
 * every tab and the island. Shared by the public page and the Business Manager
 * preview, whose root loaders both return `pageCtas`.
 */
export const ProfessionalPageCtasProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const initial = useProfessionalInitialRouteData();
    const loaded = useLoadedPageCtas() ?? null;
    const session = initial.ClientDirectorCalendarSession;
    const primaryCTA = initial.primaryCTA;

    const fallback = useMemo<PageCtasProviderProps['fallback']>(
        () => ({
            pageType: EnquiryFormPageType.Professional,
            enquiryAudience: initial.EnquiryAudience,
            enquireLabel:
                primaryCTA?.primaryCTALabelTitleCase || DEFAULT_ENQUIRE_LABEL,
            enquireLabelShort: primaryCTA?.primaryCTALabelShort || undefined,
            enquireLabelEnum: primaryCTA?.primaryCTA,
            isSupplier: isProductSupplierProfessional({
                isManufacturer: initial.IsManufacturer,
                hasProducts: initial.HasProducts,
                productsAllowed: initial.ProductsAllowed,
            }),
            fallbackSessionIds:
                session && isBookableSession(session) ? [session.ID] : [],
        }),
        [
            initial.EnquiryAudience,
            initial.IsManufacturer,
            initial.HasProducts,
            initial.ProductsAllowed,
            primaryCTA,
            session,
        ],
    );

    const enquiry = useMemo<EnquiryModalContext>(
        () => ({
            professional: initial,
            itemType: 'professional',
            siteTreeID: initial.ID,
            itemTitle: initial.Title,
            includeBranch: true,
        }),
        [initial],
    );

    // No itemTitle: a profile has no subject of its own, and naming the
    // practice made the request read as being about the one it is sent to.
    const book = useMemo<PageBookIntentBase>(
        () => ({
            professionalUrlSegment: initial.URLSegment,
            siteTreeId: initial.ID,
            professionalId: initial.ID,
            professionalName: initial.Title,
            companyLogo: initial.CompanyLogo,
            logoBackgroundColor: initial.LogoBackgroundColor,
        }),
        [
            initial.URLSegment,
            initial.ID,
            initial.Title,
            initial.CompanyLogo,
            initial.LogoBackgroundColor,
        ],
    );

    return (
        <PageCtasProvider
            loaded={loaded}
            fallback={fallback}
            enquiry={enquiry}
            book={book}
        >
            {children}
            <CtaAutoOpen />
        </PageCtasProvider>
    );
};

export interface ProfessionalPrimaryCta {
    visible: boolean;
    label: string;
    open: () => void;
    /** The page's Book button, for the inline one beside the primary. */
    book: BookCta;
    /** Show a Book button beside the primary: configured, and not already it. */
    showInlineBook: boolean;
}

/**
 * The profile's primary CTA for one surface (header, sticky cluster, island).
 * It reads the enquire wording, and opens the booking gate only when the page's
 * primary is Book a meeting — a wording such as "Book a Consultation" — so a
 * button that asks for a price or a quote always lands on the enquiry.
 */
export const useProfessionalPrimaryCta = (
    options: Pick<BookCtaOptions, 'source' | 'bookSource'> = {},
): ProfessionalPrimaryCta => {
    const { ctas } = usePageCtas();
    const enquire = useEnquireCta({ source: options.source });
    const book = useBookCta(options);
    const primaryIsBook = ctas.primary === 'book';
    // Book leads on its own words only when the enquiry is hidden; otherwise
    // the enquire wording already promises the meeting.
    const label =
        primaryIsBook && !enquire.visible ? book.label : enquire.label;

    return {
        visible: ctas.primary !== null,
        label,
        open: primaryIsBook ? book.open : () => void enquire.open(),
        book,
        showInlineBook: book.inline && !primaryIsBook,
    };
};
